import type { StoreActionApi } from 'react-sweet-state';

import { assert, AssertionError } from '@atlassian/eoc-common';
import { type PeriodDetail, PeriodType } from '@atlassian/eoc-schedule-common';

import { type ShiftAction, ShiftStatus } from '../../../common/types';
import { NowLocation } from '../../../utils/constants/schedule';
import { getNowLocation } from '../../../utils/schedule';
import { type ShiftStore } from '../state/types';
import { type ContainerProps } from '../types';

import { ACTION_TRANSITION, SELECT_TRANSITION } from './constants';

const act =
	(action: ShiftAction) =>
	({ setState, getState }: StoreActionApi<ShiftStore>) => {
		const status = getState().status;

		assert(
			status in ACTION_TRANSITION && action in ACTION_TRANSITION[status],
			new AssertionError('eoc-schedule', 'period-store: illegal state transition'),
		);

		const nextStatus = ACTION_TRANSITION[status][action];

		setState({ status: nextStatus });
	};

/**
 * Clicks a period detail item
 * @param target Period detail item to click
 * @returns Whether the item was clicked
 */
const click =
	(target: PeriodDetail) =>
	({ setState, getState }: StoreActionApi<ShiftStore>, { nowEpoch }: ContainerProps) => {
		const { startEpoch, endEpoch, type } = target;

		const state = getState();

		// TODO: pass nowEpoch to shift store
		const nowLocation = getNowLocation(startEpoch, endEpoch, nowEpoch);

		// If current card is target
		if (
			[
				ShiftStatus.Single,
				ShiftStatus.SingleOverriden,
				ShiftStatus.SingleViewOnly,
				ShiftStatus.SingleQuickOverride,
			].includes(state.status) &&
			state.cards[0].id === target.id
		) {
			setState({ status: ShiftStatus.Default, cards: [] });
			return false;
		}

		// All past items or forwarding items are single view only
		if (nowLocation === NowLocation.After || type === PeriodType.Forwarding) {
			setState({ status: ShiftStatus.SingleViewOnly, cards: [target] });
			return true;
		}

		// All overriden items that are not past are single overriden
		if (type === PeriodType.Override) {
			setState({ status: ShiftStatus.SingleOverriden, cards: [target] });
			return true;
		}

		// All other items are single
		setState({ status: ShiftStatus.Single, cards: [target] });
		return true;
	};

/**
 * Selects a period detail item
 * @param target Period detail item to select
 * @returns Whether the item was Selected
 */
const select =
	(target: PeriodDetail) =>
	({ getState, setState }: StoreActionApi<ShiftStore>, { nowEpoch }: ContainerProps) => {
		const { startEpoch, endEpoch, type } = target;

		const state = getState();

		// TODO: pass nowEpoch to shift store
		const nowLocation = getNowLocation(startEpoch, endEpoch, nowEpoch);

		// All past items, forwarding items, and overriden items are not selectable
		if (
			nowLocation === NowLocation.After ||
			type === PeriodType.Forwarding ||
			type === PeriodType.Override
		) {
			return false;
		}

		// Selected item already exists within cards then it should be removed
		if (state.cards.some((card) => card.id === target.id)) {
			setState({
				status: state.cards.length > 1 ? state.status : ShiftStatus.Default,
				cards: state.cards.filter((card) => card.id !== target.id),
			});
			return true;
		}

		// All other items are added to the selection
		setState({
			status: SELECT_TRANSITION[state.status],
			cards: [...state.cards, target],
		});
		return true;
	};

const clear =
	() =>
	({ setState }: StoreActionApi<ShiftStore>) => {
		setState({
			status: ShiftStatus.Default,
			cards: [],
		});
	};

export const actions = {
	act,
	click,
	select,
	clear,
};
